import Lightbox from 'components/base/LightBox';
import useLightbox from 'hooks/useLightbox';
import MessageAttachments from './MessageAttachments';
import AttachmentPreview from 'components/common/AttachmentPreview';
import { useAppContext } from 'providers/AppProvider';
import { formatMessageTime, formatMessageDateTime } from 'helpers/datetime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCheckDouble } from '@fortawesome/free-solid-svg-icons';

interface MessageProps {
  message: any;
  peerInitials?: string;
}

const Message = ({ message, peerInitials = 'V' }: MessageProps) => {
  const { lightboxProps, openLightbox } = useLightbox(
    message.attachments?.images || []
  );
  const {
    config: { isDark }
  } = useAppContext();

  const isAgent = message.type === 'agent';

  const sentBg = isDark ? '#e8eaed' : '#1b1c20';
  const sentText = isDark ? '#16181d' : '#ffffff';
  const recvBg = isDark ? '#26282e' : '#ffffff';
  const recvText = isDark ? '#e6e7ea' : '#101828';
  const recvBorder = isDark ? '#33363d' : '#eaecf0';
  const timeColor = isDark ? '#8a8f98' : '#98a2b3';
  const avatarBg = isDark ? '#33363d' : '#e7ebf0';
  const avatarColor = isDark ? '#cdd2da' : '#3d4a5c';

  const agentName: string | undefined = message.agentName;
  const agentAvatar: string | undefined = message.agentAvatar;
  const agentInitials =
    (agentName || '')
      .trim()
      .split(/\s+/)
      .map((w: string) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'A';

  const avatarBox: React.CSSProperties = {
    width: 28,
    height: 28,
    borderRadius: '50%',
    flexShrink: 0,
    objectFit: 'cover'
  };
  const initialsBox: React.CSSProperties = {
    ...avatarBox,
    background: avatarBg,
    color: avatarColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 500
  };

  const avatar = isAgent ? (
    agentAvatar ? (
      <img src={agentAvatar} alt={agentName || 'Agent'} style={avatarBox} />
    ) : (
      <div style={initialsBox}>{agentInitials}</div>
    )
  ) : (
    <div style={initialsBox}>{peerInitials}</div>
  );

  const bubbleStyle: React.CSSProperties = {
    padding: '9px 13px',
    fontSize: 14,
    lineHeight: 1.45,
    wordBreak: 'break-word',
    borderRadius: 14,
    ...(isAgent
      ? { background: sentBg, color: sentText, borderBottomRightRadius: 4 }
      : {
          background: recvBg,
          color: recvText,
          border: `0.5px solid ${recvBorder}`,
          borderBottomLeftRadius: 4
        })
  };

  // Offset the name/time so they sit over/under the bubble, not the avatar
  // (avatar width 28 + 8 gap = 36).
  const sideOffset: React.CSSProperties = isAgent
    ? { marginRight: 36 }
    : { marginLeft: 36 };

  return (
    <div
      className={`d-flex mb-1 ${isAgent ? 'justify-content-end' : 'justify-content-start'}`}
    >
      <div
        className="d-flex flex-column"
        style={{ maxWidth: '80%', alignItems: isAgent ? 'flex-end' : 'flex-start', minWidth: 0 }}
      >
        {isAgent && agentName && (
          <span
            style={{ fontSize: 11, fontWeight: 500, color: timeColor, marginBottom: 2, ...sideOffset }}
          >
            {agentName}
          </span>
        )}

        <div
          className="d-flex align-items-end"
          style={{ gap: 8, flexDirection: isAgent ? 'row-reverse' : 'row', minWidth: 0 }}
        >
          {avatar}
          <div style={bubbleStyle}>
            {message.content && <span>{message.content}</span>}
            {message.attachment?.type === 'image' && (
              <img
                src={message.attachment.url}
                alt="attachment"
                style={{
                  display: 'block',
                  maxWidth: 240,
                  maxHeight: 260,
                  borderRadius: 10,
                  marginTop: message.content ? 6 : 0,
                  cursor: 'pointer'
                }}
                onClick={() => window.open(message.attachment.url, '_blank')}
              />
            )}
            {message.attachment?.type === 'audio' && (
              <audio
                controls
                preload="metadata"
                src={message.attachment.url}
                style={{
                  display: 'block',
                  width: 240,
                  maxWidth: '100%',
                  marginTop: message.content ? 6 : 0
                }}
              />
            )}
            {message.attachment?.type === 'file' && (
              <a
                href={message.attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  textDecoration: 'none',
                  color: 'inherit',
                  marginTop: message.content ? 6 : 0,
                  maxWidth: 240
                }}
              >
                <span style={{ fontSize: 20 }}>📄</span>
                <span style={{ minWidth: 0 }}>
                  <span
                    style={{
                      display: 'block',
                      fontWeight: 600,
                      fontSize: 13,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {message.attachment.meta?.name || 'Download file'}
                  </span>
                  <span style={{ fontSize: 11, opacity: 0.7 }}>Download</span>
                </span>
              </a>
            )}
            {message.attachments?.images && (
              <MessageAttachments
                attachments={message.attachments.images}
                openLightbox={openLightbox}
              />
            )}
            {message.attachments?.file && (
              <AttachmentPreview
                attachment={message.attachments.file}
                variant={isAgent ? 'secondary' : 'primary'}
              />
            )}
          </div>
        </div>

        <span
          style={{
            fontSize: 10,
            color: timeColor,
            marginTop: 3,
            ...sideOffset,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4
          }}
          title={formatMessageDateTime(message.timestamp)}
        >
          {message.time || formatMessageTime(message.timestamp)}
          {isAgent && (
            <FontAwesomeIcon
              icon={message.read ? faCheckDouble : faCheck}
              style={{ fontSize: 9, color: message.read ? '#3b82f6' : timeColor }}
              title={message.read ? 'Read' : 'Sent'}
            />
          )}
        </span>
        <Lightbox {...lightboxProps} />
      </div>
    </div>
  );
};

export default Message;
